import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { HelpTask, HelpWorkflow } from '../guide.model';
import { guideInline, guideMarkdown } from '../guide-markdown';
import { GuideService, GuideTab } from '../guide.service';
import { HelpSearchHit, HelpTopicRef } from '../guide-registry';

/**
 * The Guide's side panel: this screen's jobs as steps to tick, what the
 * screen shows and the messages it can give, and a search over the manual.
 * It sits beside the screen rather than over it, so somebody can read a step
 * and click at the same time.
 */
@Component({
    selector: 'spectrum-guide-panel',
    imports: [CommonModule, FormsModule, RouterModule],
    templateUrl: './guide-panel.component.html',
    styleUrl: './guide-panel.component.scss'
})
export class SpectrumGuidePanelComponent {
    readonly guide = inject(GuideService);

    search = '';
    openTask = 0;
    private lastTopicId: string | null = null;
    private htmlCache = new Map<string, string>();

    readonly tabs: { id: GuideTab; label: string; icon: string }[] = [
        { id: 'screen', label: 'This screen', icon: 'fa-display' },
        { id: 'manual', label: 'Manual', icon: 'fa-book-open' },
    ];

    get ref(): HelpTopicRef | null {
        const ref = this.guide.current;
        // A new screen opens its first job, not whichever was open before.
        if ((ref?.topic.id || null) !== this.lastTopicId) {
            this.lastTopicId = ref?.topic.id || null;
            this.openTask = 0;
        }
        return ref;
    }

    get title(): string {
        return this.guide.current?.topic.title || this.guide.screenLabel || this.guide.moduleTitle;
    }

    get where(): string {
        return this.guide.current?.manual.title || this.guide.moduleTitle;
    }

    setTab(tab: GuideTab): void {
        this.guide.tab = tab;
    }

    md(text: string): string {
        let html = this.htmlCache.get(text);
        if (html === undefined) {
            html = guideMarkdown(text);
            this.htmlCache.set(text, html);
        }
        return html;
    }

    line(text: string): string {
        const key = '\u0000' + text;
        let html = this.htmlCache.get(key);
        if (html === undefined) {
            html = guideInline(text);
            this.htmlCache.set(key, html);
        }
        return html;
    }

    // ---- steps ---------------------------------------------------------

    toggleTask(index: number): void {
        this.openTask = this.openTask === index ? -1 : index;
    }

    isTicked(task: number, step: number): boolean {
        const ref = this.guide.current;
        return !!ref && this.guide.progress.ticked(ref.topic.id, task).includes(step);
    }

    tickedCount(task: number): number {
        const ref = this.guide.current;
        return ref ? this.guide.progress.ticked(ref.topic.id, task).length : 0;
    }

    /** The first step not yet ticked: the one the panel points at. */
    nextStep(task: HelpTask, index: number): number {
        return task.steps.findIndex((_, step) => !this.isTicked(index, step));
    }

    toggleStep(task: number, step: number): void {
        const ref = this.guide.current;
        if (ref) {
            this.guide.progress.toggleStep(ref.topic.id, task, step);
        }
    }

    resetTask(task: number, event: Event): void {
        event.stopPropagation();
        const ref = this.guide.current;
        if (ref) {
            this.guide.progress.resetTask(ref.topic.id, task);
        }
    }

    // ---- walkthroughs and links -----------------------------------------

    get workflowsHere(): { workflow: HelpWorkflow; step: number }[] {
        const ref = this.guide.current;
        return ref ? this.guide.registry.workflowsFor(ref.topic.id) : [];
    }

    get manualWorkflows(): HelpWorkflow[] {
        return this.guide.registry.home?.workflows || [];
    }

    related(id: string): HelpTopicRef | null {
        return this.guide.registry.topicById(id);
    }

    stepTopicTitle(id: string | undefined): string {
        return this.guide.registry.topicById(id)?.topic.title || '';
    }

    helpCentreLink(): { url: string; query: { [key: string]: string } } {
        const ref = this.guide.current;
        return ref
            ? { url: `/help/${ref.manual.module}`, query: { topic: ref.topic.id } }
            : { url: '/help', query: {} };
    }

    // ---- manual search -------------------------------------------------

    get hits(): HelpSearchHit[] {
        return this.search.trim().length > 1 ? this.guide.registry.search(this.search, 12) : [];
    }

    /** The module's chapters with their screens: the manual's contents, for browsing. */
    get chapters(): { id: string; title: string; topics: HelpTopicRef[] }[] {
        const home = this.guide.registry.home;
        if (home !== this.chaptersFor) {
            this.chaptersFor = home;
            this.chapterCache = home
                ? home.chapters.map(c => ({
                    id: c.id,
                    title: c.title,
                    topics: home.topics.filter(t => t.chapter === c.id).map(topic => ({ topic, manual: home })),
                })).filter(c => c.topics.length)
                : [];
        }
        return this.chapterCache;
    }
    private chaptersFor: unknown = undefined;
    private chapterCache: { id: string; title: string; topics: HelpTopicRef[] }[] = [];

    openHit(hit: HelpSearchHit): void {
        this.search = '';
        this.guide.openTopic(hit.ref);
    }
}
